
import { defineComponent, ref } from "vue";
import AppFooter from "@/components/common/AppFooter.vue";
import ConfirmationSave from "@/components/ConfirmationSave.vue";
import common from "@/config/common";
import helper from "@/utils/helper";
import { mapMutations, mapGetters } from "vuex";
import {
  Appearance,
  CssFontSource,
  PaymentIntent,
  SetupIntent,
  Stripe,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeCardNumberElementOptions,
  StripeElements,
  loadStripe,
} from "@stripe/stripe-js";
import messages from "@/config/messages";
import routesPath from "@/config/routesPath";
import ConfirmationTable from "@/components/ConfirmationTable.vue";
import * as apiClient from "@/apiclient/";
import benefitMapper from "@/utils/benefitMapper";
import iCOBRABenefit from "@/types/iCOBRABenefit";

export default defineComponent({
  components: { AppFooter, ConfirmationTable, ConfirmationSave },
  data() {
    return {
      validationMsg: "",
      helper,
      common,
      stripe: null as Stripe | null,
      elements: null as StripeElements | null,
      publishableKey: "",
      clientSecret: "",
      expectedStatus: "",
      paymentIntent: null as PaymentIntent | null,
      cardIntent: null as SetupIntent | null,
      cardNumber: null as StripeCardNumberElement | null,
      cardCvc: null as StripeCardCvcElement | null,
      cardExpiry: null as StripeCardExpiryElement | null,
      cardNumberError: "",
      cardCvcError: "",
      cardExpiryError: "",
      isLoading: false,
      totalAmount: 0,
      paymentMethods: {} as any,
      paymentSummary: {} as any,
      profileData: {} as apiClient.ProfileResponseDto,
      qualifyingBeneficiaryData: {} as apiClient.QualifyingBeneficiaryDto,
      dependentData: [] as apiClient.GetAllDependentDto[],
      documentsData: [] as apiClient.FileResponse[],
      amountTodayData: {} as apiClient.GetAmountDueTodayDto,
      monthlyPremiumData: {} as apiClient.GetMonthlyPremiumDto,
      benefitsData: [] as iCOBRABenefit[],
      allCountries: [] as apiClient.CountryResponseDto[],
      allStates: [] as apiClient.StateResponseDto[],
      getCountryName: "",
      getStateName: "",
      getMonthlyCountryName: "",
      getMonthlyStateName: "",
      genderOptions: [],
      countries: {} as any,
      accountTypes: {} as any,
      confirmationAlldata: [] as apiClient.ParticipantDetailDto,
      cardNotSelected: false,
      isStripeEnabled: false,
      showConfirmationModal: false,
      isPaymentFormValid: false,
      isCardNumberValid: false,
      isCardCvcValid: false,
      isCardExpiryValid: false,
      showNewCardDetails: false,
      savedCards: [] as apiClient.GetCardDetail[],
      selectedCardId: -1,
      cardDetail: Object as apiClient.CardDetail,
      isPayed: false as boolean,
    };
  },
  async created() {
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
  },
  computed: {
    ...mapGetters([
      "getFormDirty",
      "getEnrollment",
      "getIsPayed",
      "getPaymentIntents",
    ]),
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  async mounted() {
    this.isPayed = this.getIsPayed;

    let stripeData = await helper.getStripeEnabled();
    if (stripeData) {
      if (stripeData!.validationMsg.length > 0) {
        this.validationMsg = "You don't have access to this page";
        this.isStripeEnabled = false;
      } else {
        this.isStripeEnabled = stripeData.isStripeEnabled!;
      }
    }
    await this.getPaymentMethods();
    await this.getPaymentSummary();
    await this.getAccountTypes();
    await this.getAllData();
    await this.getLocations();
    await this.getPublishableKey();
    await this.getCardDetail();
    if (
      this.paymentSummary?.getAmountDueToday?.payMethod ===
        common.paymentMethods.Card &&
      this.isPayed === false
    ) {
      await this.getPaymentClientSecret();
    } else if (
      this.paymentSummary?.getMonthlyPremium?.payMethod ===
        common.paymentMethods.Card &&
      this.isPayed === false
    ) {
      await this.getCardClientSecret();
    }
    if (
      this.isPayed === false &&
      (this.selectedCardId === 0 || !(this.savedCards?.length > 0))
    ) {
      await this.initializeStripe();
    }
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  methods: {
    ...mapMutations(["setFormDirty", "setWasSpecialOpenEnrollmentCreated"]),
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    openConfirmationModal() {
      this.showConfirmationModal = true;
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    showCardDetails() {
      this.showNewCardDetails = !this.showNewCardDetails;
    },
    onCardChange() {
      if (this.selectedCardId === 0 || !(this.savedCards?.length > 0)) {
        this.initializeStripe();
      }
    },
    submitText() {
      if (
        this.paymentSummary?.getAmountDueToday?.payMethod ===
          common.paymentMethods.Card &&
        this.isPayed === false
      ) {
        return "PAY & SUBMIT";
      } else {
        return "SUBMIT";
      }
    },
    shouldModalVisible() {
      if (this.selectedCardId > 0) {
        return true;
      }
      if (
        this.paymentSummary?.getAmountDueToday?.payMethod ===
        common.paymentMethods.Card
      ) {
        return this.isPaymentFormValid;
      } else if (
        this.paymentSummary?.getMonthlyPremium?.payMethod ===
        common.paymentMethods.Card
      ) {
        return (
          this.isCardNumberValid &&
          this.isCardCvcValid &&
          this.isCardExpiryValid
        );
      }
      return false;
    },
    async getPublishableKey() {
      const result = await apiClient
        .stripePaymentGatewayController()
        .getApiStripepaymentgatewayPublishableKey();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.publishableKey = result.data;
      }
    },
    async deleteCardDetail(paymentMethodId: number) {
      const result = await apiClient
        .stripePaymentGatewayController()
        .deleteApiStripepaymentgatewayDeleteCardDetail({
          PaymentMethodId: paymentMethodId,
        });
      if (result.data) {
        await this.getCardDetail();
      } else if (result.error) {
        await this.getCardDetail();
        console.error(result.error);
      }
    },
    async getCardDetail() {
      const result = await apiClient
        .stripePaymentGatewayController()
        .getApiStripepaymentgatewayGetCardDetail({
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.savedCards = result.data.cardDetails!;
      }
    },
    async getPaymentClientSecret() {
      const result = await apiClient
        .stripePaymentGatewayController()
        .getApiStripepaymentgatewayPaymentIntentDetail({
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.clientSecret = result.data.secretKey!;
        if (result.data.expectedStatus) {
          this.expectedStatus = result.data.expectedStatus;
        } else {
          // For older versions of the API that don't return expected status, we should expect the payment status to be "succeeded"
          this.expectedStatus = "succeeded";
        }
      }
    },
    async getCardClientSecret() {
      const result = await apiClient
        .stripePaymentGatewayController()
        .getApiStripepaymentgatewaySetupIntentDetail({
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.clientSecret = result.data.secretKey!;
      }
    },
    async getAccountTypes(): Promise<void> {
      const result = await apiClient
        .paymentController()
        .getApiPaymentAccountType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.accountTypes = result.data.value;
      }
    },
    async getLocations() {
      let locations = await helper.getLocationDetails(
        this.profileData.countryId!,
        this.profileData.geoStateId!,
        this.monthlyPremiumData.billingCountryId!,
        this.monthlyPremiumData.billingGeoStateId!
      );
      this.getCountryName = locations.getCountryName;
      this.getMonthlyCountryName = locations.getMonthlyCountryName;
      this.getStateName = locations.getStateName;
      this.getMonthlyStateName = locations.getMonthlyStateName;
      this.countries = locations.countries;
    },
    async getAllData(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .confirmationController()
        .getApiConfirmation(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg2;
        }
        return;
      } else if (result.data) {
        this.confirmationAlldata = result.data;
        this.profileData = result.data.profile!;
        this.qualifyingBeneficiaryData = result.data.qualifyingBeneficiary!;
        this.dependentData = result.data.dependents!;
        this.documentsData = result.data.documents!;
        this.amountTodayData = result.data.getPaymentAmountDueToday!;
        this.monthlyPremiumData = result.data.getPaymentMonthlyPremium!;
        this.benefitsData = benefitMapper.mapCOBRAApiBenefitsToBenefits(
          result.data.benefits!
        );
        this.cardDetail = result.data.cardDetail!;
      }
    },
    async confirmEnrollment() {
      this.closeConfirmationModal();
      this.validationMsg = "";
      this.paymentIntent = null;

      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      if (this.isPayed) {
        this.savePaymentIntent();
        return;
      }

      if (this.selectedCardId > 0) {
        this.saveExistingCardPayment();
        return;
      }
      if (this.stripe && this.elements) {
        if (
          this.paymentSummary?.getAmountDueToday?.payMethod ===
          common.paymentMethods.Card
        ) {
          const result = await this.stripe?.confirmPayment({
            elements: this.elements,
            confirmParams: {
              return_url: `${window.location.origin}/return`,
            },
            redirect: "if_required",
          });

          if (result?.error?.message && result?.error?.payment_intent) {
            this.validationMsg = result?.error?.message + messages.errorMsg2;
            if (result?.error?.payment_intent) {
              await this.saveErrorLog(result?.error?.payment_intent.id);
            }
            this.isLoading = false;
          } else if (result?.error?.message) {
            this.validationMsg = result.error.message + messages.errorMsg2;
            this.isLoading = false;
          } else if (
            result?.paymentIntent &&
            result?.paymentIntent?.status === this.expectedStatus
          ) {
            this.paymentIntent = result?.paymentIntent;
            await this.savePaymentIntent();
          } else {
            this.validationMsg =
              "An unexpected error occurred. " + messages.errorMsg2;
            this.isLoading = false;
          }
        } else if (
          this.paymentSummary?.getMonthlyPremium?.payMethod ===
            common.paymentMethods.Card &&
          this.cardNumber
        ) {
          const result = await this.stripe?.confirmCardSetup(
            this.clientSecret,
            {
              payment_method: {
                card: this.cardNumber,
              },
            }
          );
          if (result?.error?.message) {
            this.validationMsg = result.error.message + messages.errorMsg2;
            this.isLoading = false;
          } else if (
            result?.setupIntent &&
            result?.setupIntent?.status === "succeeded"
          ) {
            this.cardIntent = result?.setupIntent;
            await this.saveCardIntent();
          } else {
            this.validationMsg =
              "An unexpected error occurred. " + messages.errorMsg2;
            this.isLoading = false;
          }
        }
      }
    },
    async saveExistingCardPayment() {
      const savedResult = await apiClient
        .stripePaymentGatewayController()
        .postApiStripepaymentgatewayTransactionsSaveCard({
          paymentMethodId: this.selectedCardId,
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });

      if (savedResult?.data?.id) {
        this.isLoading = true;
        document.getElementById("pdfContentContainer")!.style.display = "block";
        const pdfBlob: Blob = await helper.printPDF(
          document.getElementById("pdfContent")!,
          false,
          true
        );
        document.getElementById("pdfContentContainer")!.style.display = "none";
        this.afterPdfCreate(pdfBlob);
      } else if (savedResult?.error) {
        this.validationMsg = savedResult.error + messages.errorMsg;
      } else {
        this.validationMsg =
          "An unexpected error occurred. " + messages.errorMsg;
      }
    },
    async savePaymentIntentOld() {
      const savedResult = await apiClient
        .stripePaymentGatewayController()
        .postApiStripepaymentgatewaySaveTransactionDetail({
          paymentIntentId: this.paymentIntent?.id,
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });
      if (savedResult?.data?.transactionId) {
        this.isLoading = true;
        document.getElementById("pdfContentContainer")!.style.display = "block";
        const pdfBlob: Blob = await helper.printPDF(
          document.getElementById("pdfContent")!,
          false,
          true
        );
        document.getElementById("pdfContentContainer")!.style.display = "none";
        this.afterPdfCreate(pdfBlob);
      } else if (savedResult?.error) {
        this.validationMsg = savedResult.error + messages.errorMsg;
      } else {
        this.validationMsg =
          "An unexpected error occurred. " + messages.errorMsg;
      }
    },
    async savePaymentIntent() {
      this.isLoading = true;
      document.getElementById("pdfContentContainer")!.style.display = "block";
      const pdfBlob: Blob = await helper.printPDF(
        document.getElementById("pdfContent")!,
        false,
        true
      );
      document.getElementById("pdfContentContainer")!.style.display = "none";
      this.afterPdfCreate(pdfBlob);
    },
    async saveCardIntent() {
      const savedResult = await apiClient
        .stripePaymentGatewayController()
        .postApiStripepaymentgatewaySaveCardDetail({
          setupIntentId: this.cardIntent?.id,
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });
      if (savedResult?.data?.cardId) {
        document.getElementById("pdfContentContainer")!.style.display = "block";
        const pdfBlob: Blob = await helper.printPDF(
          document.getElementById("pdfContent")!,
          false,
          true
        );
        document.getElementById("pdfContentContainer")!.style.display = "none";
        this.afterPdfCreate(pdfBlob);
        this.isLoading = true;
      } else if (savedResult?.error) {
        this.validationMsg = savedResult.error + messages.errorMsg;
      } else {
        this.validationMsg =
          "An unexpected error occurred. " + messages.errorMsg;
      }
    },
    async saveErrorLog(paymentIntentId: string) {
      const savedResult = await apiClient
        .stripePaymentGatewayController()
        .postApiStripepaymentgatewaySaveCardTransLog({
          paymentIntentId,
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });
      if (savedResult?.error) {
        this.validationMsg = savedResult.error + messages.errorMsg;
      }
    },
    async initializeStripe() {
      this.stripe = await loadStripe(this.publishableKey);
      const appearance: Appearance = {
        variables: {
          fontFamily: "Roboto",
        },
        rules: {
          ".Input": {
            backgroundColor: "white",
            borderRadius: "0",
            borderColor: "#D7DBDC",
            color: "#024059",
            fontSize: "14px",
            fontWeight: "600",
            boxShadow: "0",
          },
          ".Label": {
            fontSize: "14px",
            color: "#3d484d",
          },
          ".Input--invalid": {
            border: "1px solid #FF3654",
            color: "#FF3654",
            boxShadow: "0",
          },
          ".Error": {
            fontSize: "11px",
            color: "#FF3654",
          },
        },
      };
      const fonts: CssFontSource[] = [
        {
          cssSrc: "https://fonts.googleapis.com/css?family=Roboto",
        },
      ];
      if (this.stripe && this.clientSecret) {
        if (
          this.paymentSummary?.getAmountDueToday?.payMethod ===
          common.paymentMethods.Card
        ) {
          this.elements = this.stripe?.elements({
            clientSecret: this.clientSecret,
            appearance,
            fonts,
          });
          const paymentElement = this.elements?.create("payment");
          paymentElement?.mount("#payment-element");
          paymentElement.on("change", (event) => {
            this.isPaymentFormValid = false;
            if (!event.empty) {
              this.setFormDirty(true);
            }
            if (event.complete) {
              this.isPaymentFormValid = true;
            }
          });
        } else if (
          this.paymentSummary?.getMonthlyPremium?.payMethod ===
          common.paymentMethods.Card
        ) {
          this.elements = this.stripe?.elements({ fonts });
          const options: StripeCardNumberElementOptions = {
            style: {
              base: {
                backgroundColor: "white",
                color: "#024059",
                fontSize: "14px",
                fontWeight: "600",
                fontFamily: "Roboto",
              },
            },
          };
          this.cardNumber = this.elements?.create("cardNumber", options);
          this.cardNumber?.mount("#card-number");
          this.cardNumber.on("change", (event) => {
            this.setFormDirty(true);
            this.cardNumberError = "";
            if (event.error?.message) {
              this.cardNumberError = event.error.message;
              this.isCardNumberValid = false;
            } else if (event.complete) {
              this.isCardNumberValid = true;
            }
          });
          this.cardCvc = this.elements?.create("cardCvc", options);
          this.cardCvc?.mount("#card-cvc");
          this.cardCvc.on("change", (event) => {
            this.setFormDirty(true);
            this.cardCvcError = "";
            if (event.error?.message) {
              this.cardCvcError = event.error.message;
              this.isCardCvcValid = false;
            } else if (event.complete) {
              this.isCardCvcValid = true;
            }
          });
          this.cardExpiry = this.elements?.create("cardExpiry", options);
          this.cardExpiry?.mount("#card-expiration");
          this.cardExpiry.on("change", (event) => {
            this.setFormDirty(true);
            this.cardExpiryError = "";
            if (event.error?.message) {
              this.cardExpiryError = event.error.message;
              this.isCardExpiryValid = false;
            } else if (event.complete) {
              this.isCardExpiryValid = true;
            }
          });
        }
      }
    },
    async getPaymentSummary() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.paymentController().getApiPayment(params);

      if (result.data && result.data.benefitSummary) {
        this.paymentSummary = result.data;
        helper.setBenefitSummary(result.data.benefitSummary);
        this.totalAmount = 0;
        const benefitsArr = result.data.benefitSummary;
        benefitsArr.forEach((benefit: apiClient.BenefitSummaryDto) => {
          this.totalAmount +=
            this.paymentSummary?.getAmountDueToday?.payMethod ===
            common.paymentMethods.Card
              ? benefit.initialAmount!
              : benefit.monthlyPremium!;
        });
        this.totalAmount += this.paymentSummary.convenienceFee;
      }

      if (
        this.paymentSummary?.getAmountDueToday?.payMethod !==
          common.paymentMethods.Card &&
        this.paymentSummary?.getMonthlyPremium?.payMethod !==
          common.paymentMethods.Card
      ) {
        this.cardNotSelected = true;
        this.validationMsg = "You don't have access to this page";
      }
    },
    async getPaymentMethods(): Promise<void> {
      const result = await apiClient.paymentController().getApiPaymentType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.paymentMethods = result.data.value;
      }
    },
    handleSubmit() {
      if (this.shouldModalVisible() || this.isPayed) {
        this.openConfirmationModal();
      }
    },
    async afterPdfCreate(pdfBlob: Blob) {
      var paymentIntent = this.isPayed
        ? this.getPaymentIntents[0].id
        : this.paymentIntent!.id;

      const params = {
        File: pdfBlob,
        customerParticipantId:
          this.getEnrollment.customerParticipantId.toString(),
        paymentIntentId: paymentIntent,
      };
      const result = await apiClient
        .confirmationController()
        .putApiConfirmation(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg2;
        }
        return;
      } else if (result.data) {
        sessionStorage.setItem(
          common.storageVariables.applicationSubmitted,
          "yes"
        );
        sessionStorage.setItem(common.storageVariables.isSubmitted, "yes");
        this.setWasSpecialOpenEnrollmentCreated(
          result.data.specialOpenEnrollmentCreated
        );
        this.setFormDirty(false);
        this.$router.push({ name: routesPath.applicationSuccessful });
      }
      this.isLoading = false;
    },
  },
});
